import {
  Component,
  computed,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';
import { SubHeaderService } from '../../../../../shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ViewService } from '../../../../../shared/store/view/views.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormService } from '../../../../../shared/store/form/form.service';
import { filter, Observable } from 'rxjs';
import { View } from '../../../../../shared/store/view/types';
import {
  InsuranceClaimFormModel,
  InsuranceClaimModel,
} from '../../../../../shared/store/insurance-claim/types';
import { InsuranceClaimService } from '../../../../../shared/store/insurance-claim/insurance-claim.service';
import { take } from 'rxjs/operators';
import { Entity } from '../../../../../shared/store/view/types';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../../../shared/constants/date.constants';
import { debugLog } from '../../../../../shared/pipe/rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-insurance-claim-form-content',
  templateUrl: './insurance-claim-form-content.component.html',
  styleUrls: ['./insurance-claim-form-content.component.scss'],
  standalone: false,
})
export class InsuranceClaimFormContentComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  insuranceClaimModel$: Observable<InsuranceClaimModel>;
  insuranceClaimInstance$: Observable<InsuranceClaimFormModel>;

  insuranceClaimForm: FormGroup;

  isEditMode: boolean = false;
  insuranceClaimUniqueId: string;
  insuranceClaimId: number;

  /** Gestionnaire typeahead domain (active, confirmed sales-team members). */
  salesPersonDomain = 'UserAspNetUserEmailConfirmed=true,SalesTeamActive=true';

  /** Contract id passed by the "Déclarer un sinistre" action via the navigation `info` extra. */
  readonly contractIdFromContext = signal(0);
  readonly productCategoryFromInstance = signal(0);

  readonly onInsuranceClaimIdFilled = output<number>();

  /**
   * Gridify domain for the claim type typeahead. Recomputed when the product category coming from
   * the navigation context is known — without a category, only the active filter is applied.
   */
  readonly claimTypeDomain = computed(() => {
    const productCategoryId = this.productCategoryFromInstance();
    return `Active=true,ProductCategoryId=${productCategoryId}`;
  });

  protected readonly Entity = Entity;
  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly insuranceClaimService: InsuranceClaimService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.insuranceClaimModel$ = this.insuranceClaimService.getByUniqueId.value$;
    this.insuranceClaimInstance$ =
      this.insuranceClaimService.createInstance.value$;

    // The contract is passed through the RouterLink `info` extra (must be read during navigation,
    // i.e. in the constructor), not via query params.
    const info = this.router.currentNavigation()?.extras?.info as
      | { contractId?: number; productCategoryId?: number }
      | undefined;
    this.contractIdFromContext.set(
      info && info['contractId'] ? +info['contractId'] : 0,
    );
  }

  ngOnInit(): void {
    this.insuranceClaimForm = this.formBuilder.group({
      name: [null],
      contractId: [null, [Validators.required]],
      claimTypeId: [null, [Validators.required]],
      occurrenceDate: [null],
      declarationDate: [null],
      estimatedAmount: [null],
      description: [''],
      // Gestionnaire = retrocession beneficiary (defaulted server-side from the contract manager).
      salesPersonId: [null, [Validators.required]],
      liabilityRate: [null],
      deductibleAmount: [null],
    });

    this.insuranceClaimUniqueId = this.route.snapshot.params['uniqueId'];
    this.isEditMode = !!this.insuranceClaimUniqueId;

    // Create from a contract: contractId comes from the navigation `info` extra.
    const contractIdParam = this.contractIdFromContext();
    if (!this.isEditMode && contractIdParam) {
      this.insuranceClaimForm.patchValue({ contractId: contractIdParam });
      this.insuranceClaimService.createInstance.call(contractIdParam);
    }

    if (this.isEditMode)
      this.insuranceClaimService.getByUniqueId.call(
        this.insuranceClaimUniqueId,
      );

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'insuranceClaimCreate' || x == 'insuranceClaimUpdate')
          this.onSubmit();
        if (x == 'insuranceClaimList') {
          this.formService.clear();
          this.router.navigate(['/Insurance/InsuranceClaims/']);
        }
        if (x == 'insuranceClaimDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Insurance/InsuranceClaims/Details',
            this.insuranceClaimUniqueId,
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
        if (!this.isEditMode)
          this.breadcrumbService.set(
            '@insuranceClaimCreate',
            x.breadcrumbCreate,
          );
      });

    this.insuranceClaimModel$
      .pipe(
        filter(() => this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@insuranceClaimEdit', x.name);
        this.contractIdFromContext.set(x.contractId);
        this.insuranceClaimId = x.id;
        this.onInsuranceClaimIdFilled.emit(this.insuranceClaimId);
        this.productCategoryFromInstance.set(x.contractProductCategoryId);

        this.insuranceClaimForm.patchValue({
          name: x.name,
          contractId: x.contractId,
          claimTypeId: x.claimTypeId,
          occurrenceDate: x.occurrenceDate,
          declarationDate: x.declarationDate,
          estimatedAmount: x.estimatedAmount,
          description: x.description,
          salesPersonId: x.salesPersonId,
          liabilityRate: x.liabilityRate,
          deductibleAmount: x.deductibleAmount,
        });
      });

    this.insuranceClaimInstance$
      .pipe(
        filter(() => !this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.productCategoryFromInstance.set(x.contractProductCategoryId);
        this.insuranceClaimForm.patchValue({
          claimTypeId: x.claimTypeId == 0 ? null : x.claimTypeId,
          occurrenceDate: x.occurrenceDate,
          declarationDate: x.declarationDate,
          estimatedAmount: x.estimatedAmount,
          description: x.description,
          salesPersonId: x.salesPersonId,
          liabilityRate: x.liabilityRate,
          deductibleAmount: x.deductibleAmount,
        });
      });
  }

  onSubmit() {
    if (this.insuranceClaimForm.invalid) {
      this.insuranceClaimForm.markAllAsTouched();
      this.formService.setEntityErrors('INSURANCE_CLAIM');
      this.formService.countErrors(this.insuranceClaimForm, true);
      return;
    }

    const insuranceClaimFormModel: InsuranceClaimFormModel = {
      id: this.insuranceClaimId ? +this.insuranceClaimId : 0,
      uniqueId: this.insuranceClaimUniqueId ? this.insuranceClaimUniqueId : '',
      name: this.insuranceClaimForm.value.name!,
      contractId: this.insuranceClaimForm.value.contractId!,
      claimTypeId: this.insuranceClaimForm.value.claimTypeId!,
      occurrenceDate: this.insuranceClaimForm.value.occurrenceDate!,
      declarationDate: this.insuranceClaimForm.value.declarationDate!,
      estimatedAmount:
        this.insuranceClaimForm.value.estimatedAmount === ''
          ? null
          : this.insuranceClaimForm.value.estimatedAmount!,
      description: this.insuranceClaimForm.value.description!,
      salesPersonId: this.insuranceClaimForm.value.salesPersonId!,
      contractProductCategoryId: this.productCategoryFromInstance(),
      liabilityRate:
        this.insuranceClaimForm.value.liabilityRate === ''
          ? null
          : this.insuranceClaimForm.value.liabilityRate!,
      deductibleAmount:
        this.insuranceClaimForm.value.deductibleAmount === ''
          ? null
          : this.insuranceClaimForm.value.deductibleAmount!,
    };

    let result$: Observable<InsuranceClaimModel>;
    if (this.isEditMode)
      result$ = this.insuranceClaimService.update.call(insuranceClaimFormModel);
    else result$ = this.insuranceClaimService.add.call(insuranceClaimFormModel);

    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Insurance/InsuranceClaims/Details',
          result.uniqueId,
        ]);
      });
  }

  ngOnDestroy(): void {
    this.insuranceClaimService.add.reset();
    this.insuranceClaimService.update.reset();
    if (this.isEditMode) {
      this.insuranceClaimService.getByUniqueId.reset();
    }
    this.breadcrumbService.set('@insuranceClaimEdit', ' ');
    this.breadcrumbService.set('@insuranceClaimCreate', ' ');
  }
}
